import { useEffect, useRef } from 'react';

/**
 * Calls `onMatch` when the user types `sequence`, one key after the other, outside of any editable field. Keys typed
 * with Ctrl, Cmd or Alt, or already handled by a component (`preventDefault`), are ignored.
 * Keys are `KeyboardEvent.key` values, letters in lower case.
 *
 * @example
 * useKeySequence(['ArrowUp', 'ArrowDown', 'b', 'a'], () => setIsEasterEggVisible(true));
 */
export function useKeySequence(sequence: string[], onMatch: () => void) {
    const onMatchRef = useRef(onMatch);
    onMatchRef.current = onMatch;
    const sequenceKey = JSON.stringify(sequence);

    useEffect(() => {
        const keys: string[] = JSON.parse(sequenceKey);
        let typed: string[] = [];

        const handleKeyDown = (event: KeyboardEvent) => {
            if (isIgnored(event)) return;
            typed = [...typed, event.key.length === 1 ? event.key.toLowerCase() : event.key].slice(-keys.length);

            if (typed.length === keys.length && typed.every((key, index) => key === keys[index])) {
                typed = [];
                onMatchRef.current();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [sequenceKey]);
}

const isIgnored = (event: KeyboardEvent) =>
    event.defaultPrevented ||
    event.isComposing ||
    event.ctrlKey ||
    event.metaKey ||
    event.altKey ||
    isEditable(event.target);

const isEditable = (target: EventTarget | null) =>
    target instanceof HTMLElement &&
    (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
